import { useQuery } from "@apollo/client/react";
import * as React from "react";

import {
  entityFromMemberOrTeam,
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";

import { connectionRows } from "./entities/types";
import type { WorkspaceUserRow } from "./entities/rows";
import { GetWorkspaceUsersDocument } from "./operations";
import { useWorkspaceData } from "./workspace-data-provider";

/**
 * On-demand resolution for member ids the workspace bootstrap does not carry.
 *
 * `useWorkspaceData().userMap` is ONE PAGE — `WorkspaceData` asks for
 * `users(limit: 500)` — so in a workspace with more members than that, an id
 * outside the page resolves to nothing and every caller falls through to its own
 * fallback, usually the raw id. This is not a name problem and not a permission
 * problem: the page is ordered alphabetically by name-or-email, so WHICH members
 * resolve is decided by their position in the alphabet. A 1654-member workspace
 * resolved the first 500 and printed ids for the rest.
 *
 * Raising the limit is not the fix — it grows the bootstrap payload every page
 * load pays, and only moves the threshold. Instead any surface holding a member
 * id registers it here, the ids the bootstrap map cannot answer are fetched in
 * ONE batched query, and the answers are shared. Nothing queries per row.
 *
 * `request` is safe to call DURING RENDER, which is what lets a synchronous
 * `resolve(entityType, id)` — the shape every automation wording helper uses —
 * take part: it registers the id, returns null for that pass, and renders the
 * real label once the batch lands.
 */
export type MemberDirectory = {
  /** Register member ids to resolve. Safe during render; unknown ids batch. */
  request: (ids: readonly (string | null | undefined)[]) => void;
  /** The `EntityData` for a member-or-team id, or null while unresolved. */
  entityOf: (id: string) => EntityData | null;
  /** Its display label (name → email → username), or null while unresolved. */
  labelOf: (id: string) => string | null;
  /** Whether a requested id is still unresolved because its batch has not
   *  landed — false once the answer is in, found or not. */
  pendingOf: (id: string) => boolean;
  /** Whether the server has answered for an id and holds no member for it.
   *  False while unasked or in flight, so a caller retiring dead ids never
   *  retires one that is only slow. */
  missingOf: (id: string) => boolean;
};

const MemberDirectoryContext = React.createContext<MemberDirectory | null>(null);
MemberDirectoryContext.displayName = "MemberDirectoryContext";

export function MemberDirectoryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { userMap, teamMap } = useWorkspaceData();

  // Ids registered since the last flush. A ref, not state, so a render-phase
  // `request` neither triggers a re-render nor breaks React's rules.
  const pendingRef = React.useRef<Set<string>>(new Set());
  // Every id already accounted for — resolved from the bootstrap maps or handed
  // to the query. Keeps `request` O(1) and stable across renders.
  const seenRef = React.useRef<Set<string>>(new Set());
  const [wanted, setWanted] = React.useState<string[]>([]);

  // The maps as of the latest render, for a flush that runs outside render and
  // must not close over a stale copy.
  const mapsRef = React.useRef({ userMap, teamMap });
  mapsRef.current = { userMap, teamMap };

  const flushScheduled = React.useRef(false);

  // Drains the pending ids into the query's id set.
  //
  // THE FLUSH IS SCHEDULED BY `request`, NOT BY AN EFFECT. An effect — even a
  // dependency-free one — only runs when the component that OWNS it renders, and
  // this provider sits at the top of the tree: a canvas node registering an id
  // three levels down re-renders the node, not the provider, so its effect never
  // fires and the id strands in `pendingRef` forever. That mistake shipped once,
  // and it looked like it worked because a child mounted WITH the provider does
  // flush on the initial mount — every registration after that did not.
  const flush = React.useCallback(() => {
    flushScheduled.current = false;
    if (pendingRef.current.size === 0) return;
    const { userMap: users, teamMap: teams } = mapsRef.current;
    const fetchable: string[] = [];
    for (const id of pendingRef.current) {
      seenRef.current.add(id);
      // Already in the bootstrap page (or it is a team) — never spend a query.
      if (users.has(id) || teams.has(id)) continue;
      fetchable.push(id);
    }
    pendingRef.current.clear();
    if (fetchable.length > 0) {
      setWanted((prev) => [...prev, ...fetchable].sort());
    }
  }, []);

  const request = React.useCallback(
    (ids: readonly (string | null | undefined)[]) => {
      let added = false;
      for (const id of ids) {
        if (!id || seenRef.current.has(id) || pendingRef.current.has(id)) continue;
        pendingRef.current.add(id);
        added = true;
      }
      // One flush per batch of sightings, on a macrotask. `request` is called
      // during render, so the state update has to leave the render phase first —
      // a `setTimeout` is unambiguously outside it, where a microtask can still
      // land inside React's work loop and warn about updating while rendering.
      if (added && !flushScheduled.current) {
        flushScheduled.current = true;
        setTimeout(flush, 0);
      }
    },
    [flush],
  );

  // The full set each time, so one result answers every id asked for so far and
  // there is no cache to accumulate by hand. Hoisted per the "no inline
  // variables" rule — a fresh object each render re-subscribes forever.
  const variables = React.useMemo(
    () => ({ where: { userId: { hasSome: wanted } }, limit: wanted.length }),
    [wanted],
  );
  const { data, previousData, loading } = useQuery(GetWorkspaceUsersDocument, {
    variables,
    skip: wanted.length === 0,
  });

  // `previousData` bridges the gap while a GROWN id set is in flight. Registering
  // one more id changes `variables`, and Apollo has no cache entry for the new
  // set, so `data` goes undefined mid-request — which used to empty this map and
  // revert every name already on screen to its caller's raw-id fallback, all at
  // once. Falling back is sound rather than merely better-looking: `wanted` only
  // ever grows and each request asks for the WHOLE set, so the last completed
  // response answers every id requested before it.
  const resolved = React.useMemo(() => {
    const map = new Map<string, WorkspaceUserRow>();
    for (const row of connectionRows<WorkspaceUserRow>((data ?? previousData)?.users)) {
      map.set(row.userId, row);
    }
    return map;
  }, [data, previousData]);

  const wantedSet = React.useMemo(() => new Set(wanted), [wanted]);

  const value = React.useMemo<MemberDirectory>(() => {
    const entityOf = (id: string): EntityData | null => {
      const known = entityFromMemberOrTeam({ userMap, teamMap }, id);
      if (known) return known;
      const row = resolved.get(id);
      return row ? { kind: "user", id, user: row } : null;
    };
    return {
      request,
      entityOf,
      labelOf: (id: string) => {
        const entity = entityOf(id);
        return entity ? resolveEntityLabel(entity) : null;
      },
      pendingOf: (id: string) =>
        !entityOf(id) &&
        (pendingRef.current.has(id) || (loading && wantedSet.has(id))),
      missingOf: (id: string) =>
        !entityOf(id) && wantedSet.has(id) && !loading && data != null,
    };
  }, [userMap, teamMap, resolved, request, loading, wantedSet, data]);

  return (
    <MemberDirectoryContext.Provider value={value}>
      {children}
    </MemberDirectoryContext.Provider>
  );
}

/**
 * The member directory. Use it wherever a surface holds a member id and must
 * WORD it — a resolver, a caption, a summary — rather than reading
 * `useWorkspaceData().userMap` directly, which only answers for the members that
 * happen to sit inside the bootstrap page.
 */
export function useMemberDirectory(): MemberDirectory {
  const ctx = React.useContext(MemberDirectoryContext);
  if (!ctx) {
    throw new Error("useMemberDirectory must be used within a MemberDirectoryProvider");
  }
  return ctx;
}

/**
 * A resolver for a member-or-team id that registers what it is asked about, so a
 * caller can stay a plain synchronous `(id) => string | null`.
 */
export function useMemberLabelResolver(): (id: string) => string | null {
  const { request, labelOf } = useMemberDirectory();
  return React.useCallback(
    (id: string): string | null => {
      request([id]);
      return labelOf(id);
    },
    [request, labelOf],
  );
}

/**
 * The `EntityData` twin of `useMemberLabelResolver`, for a surface that renders
 * a member id as a CHIP rather than as words. Same contract: it registers the
 * id it is asked about and answers null until the batch lands, so a caller
 * stays a plain synchronous `(id) => EntityData | null` — which is what lets an
 * aggregation hook resolve its keys inside a `useMemo` and re-derive when the
 * names arrive, since the resolver's identity changes with them.
 */
export function useMemberEntityResolver(): (id: string) => EntityData | null {
  const { request, entityOf } = useMemberDirectory();
  return React.useCallback(
    (id: string): EntityData | null => {
      request([id]);
      return entityOf(id);
    },
    [request, entityOf],
  );
}
